import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaServer, FaNetworkWired, FaLock, FaCheckCircle, FaArrowRight, FaTimes } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const DataCenterPage = () => {
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [currentImage, setCurrentImage] = useState(0);

    const galleryImages = [
        { src: '/dataCenterImages/data-center-main.png', alt: 'Data Center Facility' },
        { src: '/dataCenterImages/data-center-servers.png', alt: 'Server Infrastructure' },
        { src: '/dataCenterImages/structured-cabling.png', alt: 'Structured Cabling' },
        { src: '/dataCenterImages/access-control.png', alt: 'Access Control Systems' },
        { src: '/dataCenterImages/data-center-cooling.png', alt: 'Cooling Systems' }
    ];

    const services = [
        {
            icon: <FaServer size={32} />,
            title: 'Data Center',
            description: 'State-of-the-art data center infrastructure with 24/7 monitoring.',
            link: '/data-center',
            gradient: 'from-blue-600 to-cyan-500'
        },
        {
            icon: <FaNetworkWired size={32} />,
            title: 'Structured Cabling',
            description: 'Professional cabling solutions for optimal network performance.',
            link: '/services',
            gradient: 'from-cyan-500 to-teal-500'
        },
        {
            icon: <FaLock size={32} />,
            title: 'Access Control',
            description: 'Advanced security systems to protect your critical infrastructure.',
            link: '/services',
            gradient: 'from-indigo-600 to-blue-500'
        }
    ];

    const whyChooseUs = [
        {
            title: 'Reliable Service Delivery',
            description: 'Consistent, dependable service with guaranteed uptime and performance.'
        },
        {
            title: 'Extended Workforce',
            description: 'Access to our network of skilled professionals across North America.'
        },
        {
            title: 'Tech-Focused Philosophy',
            description: 'Cutting-edge solutions powered by the latest technologies.'
        }
    ];

    const openLightbox = (index) => {
        setCurrentImage(index);
        setLightboxOpen(true);
    };

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 py-24 overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                {/* Gradient Orbs */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    {/* Breadcrumb */}
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-cyan-400">Data Center</span>
                    </nav>

                    <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                        Data <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Center</span>
                    </h1>
                    <p className="text-xl text-gray-300 max-w-3xl">
                        Powering the digital world with state-of-the-art infrastructure
                    </p>
                </div>
            </section>

            {/* Introduction Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
                                <span className="text-blue-600 text-sm font-bold tracking-wider">ENTERPRISE SOLUTIONS</span>
                            </div>
                            <h2 className="text-4xl font-bold text-gray-900 mb-6">
                                State-of-the-Art Data Center Infrastructure
                            </h2>
                            <p className="text-gray-600 leading-relaxed mb-6">
                                At Nextgen, we specialize in designing and manufacturing state-of-the-art data center infrastructure that powers the digital world. With decades of engineering expertise, we deliver scalable, high-performance solutions that support the global demand for secure, reliable, and energy-efficient data environments.
                            </p>
                            <p className="text-gray-600 leading-relaxed mb-8">
                                From modular data center components to fully integrated systems, our products are built with a relentless focus on quality, innovation, and operational excellence. Whether you're building hyperscale facilities or edge data centers, we help you stay ahead of the curve with cutting-edge technology and expert support.
                            </p>
                            <Link
                                to="/contact"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-full font-semibold hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                            >
                                Get Started
                                <FaArrowRight size={14} />
                            </Link>
                        </div>
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl transform rotate-3 opacity-20"></div>
                            <img
                                src="/dataCenterImages/data-center-main.png"
                                alt="Data Center"
                                className="relative rounded-3xl shadow-2xl w-full object-cover"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Gallery Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
                            <span className="text-blue-600 text-sm font-bold tracking-wider">GALLERY</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Our Facilities
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Explore our world-class data center infrastructure
                        </p>
                    </div>

                    {/* Gallery Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        {galleryImages.map((image, index) => (
                            <div
                                key={index}
                                onClick={() => openLightbox(index)}
                                className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300"
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                                    <span className="text-white text-sm font-medium">{image.alt}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Lightbox */}
            {lightboxOpen && (
                <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4">
                    <button
                        onClick={() => setLightboxOpen(false)}
                        className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
                    >
                        <FaTimes size={32} />
                    </button>
                    <img
                        src={galleryImages[currentImage].src}
                        alt={galleryImages[currentImage].alt}
                        className="max-w-full max-h-[90vh] object-contain rounded-lg"
                    />
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                        {galleryImages.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentImage(index)}
                                className={`w-3 h-3 rounded-full transition-colors ${index === currentImage ? 'bg-cyan-400' : 'bg-white/50 hover:bg-white'
                                    }`}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Services Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 border border-blue-200 rounded-full">
                            <span className="text-blue-600 text-sm font-bold tracking-wider">OUR SERVICES</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
                            What We Offer
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <Link
                                key={index}
                                to={service.link}
                                className="group relative bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-2xl hover:shadow-blue-500/10 transition-all duration-500 hover:-translate-y-2"
                            >
                                <div className="p-8">
                                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                        {service.icon}
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                                        {service.title}
                                    </h3>
                                    <p className="text-gray-600 leading-relaxed">
                                        {service.description}
                                    </p>
                                </div>
                                <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${service.gradient} transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left`}></div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 relative overflow-hidden">
                {/* Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <div className="text-center mb-16">
                        <div className="inline-block mb-4 px-4 py-2 bg-white/10 border border-white/20 rounded-full">
                            <span className="text-cyan-400 text-sm font-bold tracking-wider">WHY CHOOSE NEXTGEN</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-white">
                            The NextGen Advantage
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, index) => (
                            <div
                                key={index}
                                className="group bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:bg-white/10 transition-all duration-300"
                            >
                                <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-400 to-blue-500 flex items-center justify-center mb-6">
                                    <FaCheckCircle className="text-white" size={24} />
                                </div>
                                <h3 className="text-xl font-bold text-white mb-3">
                                    {item.title}
                                </h3>
                                <p className="text-gray-300 leading-relaxed">
                                    {item.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-cyan-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                        Ready to Build Your Data Center?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8">
                        Contact us today for a consultation on your data center infrastructure needs.
                    </p>
                    <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 px-8 py-4 bg-white text-blue-600 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all duration-300 hover:-translate-y-1"
                    >
                        Contact Us Today
                        <FaArrowRight />
                    </Link>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default DataCenterPage;
